import {
    CustomAxiosError,
    MediaProgram,
    WorkflowStepDetail,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import {
    JoinWorkflowParameters,
    useJoinWorkflow,
} from '@rhg/hooks/use-join-workflow';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { logger } from '@rhg/libs/logger';
import { MutateOptions } from '@rhg/libs/react-query';
import { useCallback } from 'react';
import { useResetPackClassProgress } from '../use-reset-program';
import { useUnsponsoredDialog } from '../use-unsponsored-dialog';

interface Props {
    program: MediaProgram | undefined;
    workflow: WorkflowWorkflowInfo | undefined;
    programType: 'clinical_program' | undefined;
    packClass: string | undefined;
}

export function useJoinProgramUi({ program, workflow, packClass }: Props) {
    const { hasPermission } = useActiveCircle();
    const { joinWorkflow, isPending: isJoinPending } = useJoinWorkflow();
    const { resetPackClassProgress, isPending: isResetPackPending } =
        useResetPackClassProgress();
    const { showRegistrationDialog } = useRegistrationDialog();
    const { showUnsponsoredDialog } = useUnsponsoredDialog();
    const isAbleToJoin = hasPermission('clinical_program', 'c');

    const joinProgram = useCallback(
        async (
            willResetProgress: boolean,
            options?: MutateOptions<
                WorkflowStepDetail,
                unknown,
                JoinWorkflowParameters,
                unknown
            >
        ) => {
            if (!isAbleToJoin) {
                if (await showUnsponsoredDialog()) {
                    showRegistrationDialog({
                        currentStep: RegistrationFlowStep.AccessCode,
                        registrationFlow: RegistrationFlow.ExistingRegistration,
                    });
                }
                return;
            }
            if (!workflow) {
                const error = { response: { status: 404 } } as CustomAxiosError;
                logger.error(
                    'no workflow when joining program',
                    { programId: program?.id },
                    error
                );
                options?.onError?.(
                    error,
                    { workflow },
                    'custom error throwing context'
                );
                return;
            }

            joinWorkflow(
                { workflow },
                {
                    ...options,
                    onSuccess: (...args) => {
                        if (packClass && willResetProgress) {
                            resetPackClassProgress(
                                { packClass, programId: program?.id },
                                {
                                    onSuccess: () => {
                                        options?.onSuccess?.(...args);
                                    },
                                    onError: () => {
                                        options?.onError?.(...args);
                                    },
                                }
                            );
                        } else {
                            options?.onSuccess?.(...args);
                        }
                    },
                }
            );
        },
        [
            isAbleToJoin,
            workflow,
            showUnsponsoredDialog,
            showRegistrationDialog,
            program?.id,
            joinWorkflow,
            packClass,
            resetPackClassProgress,
        ]
    );

    return { joinProgram, isPending: isJoinPending || isResetPackPending };
}
